import { values as LIBELLES } from '../../../contexts/LanguageContext'

/* ⛔ CERTIFICAT DE REUSSITE D'UN MODULE : LE MODELE UNIQUE.
   Etape 1 (06/10/2026) : maquette validee par Nawal. Etape 2 (06/10/2026) :
   VERSION NOIRE seule (la claire n'est plus utilisee), BADGE « valide » en
   haut a la place du R pixellise (juge « pixelise » par Nawal), « Délivré le »
   + date du TELECHARGEMENT a la place de la date de completion.

   UNE composition, DEUX rendus. `composerCertificat` decrit la page en
   elements simples (rectangles, traces, textes, images), en millimetres, sur
   une page A4 paysage. L'ecran (`CertificatReussite.js`, en SVG) et le PDF
   (`pdfCertificat.js`, jsPDF) dessinent les MEMES elements : ce que
   l'apprenant voit est ce qu'il telecharge.

   Charte Rising Up (guide de style v1.0, mai 2026) : Questrial 400 seul, la
   hierarchie par la taille et la couleur, interlettrage negatif sur les
   titres, petit titre en capitales orange a +0,14 em ; Ink Black, Burnt
   Orange seul accent ; coins carres ; carres deco en coin (bord en L),
   encadre a bordure gauche orange.

   La largeur des textes vient de `mesurer(texte, tailleMm)` : la composition
   ne connait pas la police, l'appelant la mesure (canevas du navigateur). */

export const PAGE = { largeur: 297, hauteur: 210 }
const CX = PAGE.largeur / 2

export const COULEURS = {
  encre: '#0A0A0B',
  orange: '#E26438',
  blanc: '#FFFFFF',
  cremeChaud: '#FAF1E5',
  texte: '#2A2520',
  texteMuet: '#8A8480',
}

/* La version noire, seule retenue (Nawal, 06/10/2026). */
const T = {
  fond: COULEURS.encre,
  heros: COULEURS.blanc,           // le nom, le module, la date
  texte: COULEURS.cremeChaud,
  muet: COULEURS.texteMuet,
  filet: COULEURS.texte,
  coin: COULEURS.cremeChaud,
  badge: COULEURS.orange,
  coche: COULEURS.blanc,
}

/* Proportions des images fournies (largeur / hauteur, en pixels). */
export const RATIO_LOGO = 428 / 80              // images/logoBlanc-pdf.png
export const RATIO_SIGNATURE = 1200 / 673       // images/signature-nawal-blanche.png, fournie par Nawal

const libelle = (cle, langue) => LIBELLES?.certificatReussite?.[cle]?.[langue] ?? LIBELLES?.certificatReussite?.[cle]?.fr ?? ''
/* `capitales` et `couper` sont exportees pour le bilan soft skills
   (Bilan/modeleBilan.js, 07/10/2026), qui les reutilise telles quelles. */
export const capitales = (texte, langue) => String(texte).toLocaleUpperCase(langue === 'en' ? 'en' : 'fr')

/* « 6 octobre 2026 », « 1er octobre 2026 » ; « 6 October 2026 ».
   Une date seule (« 2026-10-06 ») est un jour du calendrier ; un instant
   (Date, horodatage) est lu a l'heure de Paris, celle de l'organisme
   emetteur. Rien si la date est illisible. */
export const formaterDate = (date, langue = 'fr') => {
  if (date === null || date === undefined || date === '') return null
  const calendaire = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return null
  const parties = new Intl.DateTimeFormat(langue === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: calendaire ? 'UTC' : 'Europe/Paris',
  }).formatToParts(d)
  const lire = (type) => parties.find((p) => p.type === type)?.value
  const jour = parseInt(lire('day'), 10)
  if (langue === 'en') return `${jour} ${lire('month')} ${lire('year')}`
  return `${jour === 1 ? '1er' : jour} ${lire('month')} ${lire('year')}`
}

/* Coupe un texte en lignes d'au plus `largeurMax` (mm), sur les espaces. */
export const couper = (texte, taille, esp, largeurMax, largeur) => {
  const mots = String(texte).split(/\s+/).filter(Boolean)
  const lignes = []
  let courante = ''
  mots.forEach((mot) => {
    const essai = courante ? courante + ' ' + mot : mot
    if (!courante || largeur(essai, taille, esp) <= largeurMax) {
      courante = essai
    } else {
      lignes.push(courante)
      courante = mot
    }
  })
  if (courante) lignes.push(courante)
  return lignes
}

/* ------------------------------------------------------------------ le badge
   Rosette « validee » (structure du certificat micro1 que Nawal a montre) :
   un cercle festonne en aplat orange, une coche blanche au centre. Sans halo
   ni degrade. Tout est TRACE (polygones), donc net a tout zoom dans le PDF.
   Le festonnage : r(t) = R (1 + a cos(n t)), echantillonne finement. */
export const BADGE = { lobes: 12, amplitude: 0.055, points: 480 }

export const contourRosette = (cx, cy, rayon, { lobes, amplitude, points } = BADGE) => {
  const boucle = []
  for (let i = 0; i < points; i++) {
    const t = (2 * Math.PI * i) / points - Math.PI / 2
    const r = rayon * (1 + amplitude * Math.cos(lobes * t))
    boucle.push([cx + r * Math.cos(t), cy + r * Math.sin(t)])
  }
  return boucle
}

/* La coche : une ligne brisee de deux segments, epaisse, extremites coupees
   droit et angle vif (coins carres de la charte), convertie en polygone. */
const intersection = (p, d, q, e) => {
  const det = d[0] * e[1] - d[1] * e[0]
  const t = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / det
  return [p[0] + t * d[0], p[1] + t * d[1]]
}
export const contourCoche = (cx, cy, rayon, epaisseurRel = 0.17) => {
  const P = [[-0.40, 0.02], [-0.12, 0.30], [0.42, -0.26]].map(([x, y]) => [cx + x * rayon, cy + y * rayon])
  const h = (epaisseurRel * rayon) / 2
  const dir = (a, b) => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]); return [(b[0] - a[0]) / l, (b[1] - a[1]) / l] }
  const d1 = dir(P[0], P[1]), d2 = dir(P[1], P[2])
  const n1 = [-d1[1], d1[0]], n2 = [-d2[1], d2[0]]
  const dec = (p, n, s) => [p[0] + s * h * n[0], p[1] + s * h * n[1]]
  const angle = (s) => intersection(dec(P[0], n1, s), d1, dec(P[1], n2, s), d2)
  return [dec(P[0], n1, 1), angle(1), dec(P[2], n2, 1), dec(P[2], n2, -1), angle(-1), dec(P[0], n1, -1)]
}

export const composerCertificat = ({
  prenomNom,
  nomModule,
  dateDelivrance,
  langue = 'fr',
  exemple = false,
  mesurer,
}) => {
  if (typeof mesurer !== 'function') throw new Error('composerCertificat : il faut une fonction mesurer(texte, tailleMm)')
  const elements = []

  /* largeur visible d'un texte : avances des glyphes + interlettrage entre
     les caracteres (pas apres le dernier) */
  const largeur = (texte, taille, esp = 0) => {
    const n = Array.from(String(texte)).length
    return mesurer(String(texte), taille) + esp * taille * Math.max(0, n - 1)
  }
  const texte = (t, { x, y, taille, couleur, esp = 0, ancre = 'milieu', role }) => {
    const l = largeur(t, taille, esp)
    const gauche = ancre === 'milieu' ? x - l / 2 : ancre === 'droite' ? x - l : x
    elements.push({ type: 'texte', texte: String(t), x: gauche, y, taille, couleur, espacement: esp * taille, largeur: l, role })
    return l
  }
  const rect = (x, y, l, h, couleur, role) => elements.push({ type: 'rect', x, y, l, h, couleur, role })
  const trace = (boucles, couleur, role) => elements.push({ type: 'trace', boucles, couleur, role, y: 0 })

  /* ---------------------------------------------------------------- fond */
  rect(0, 0, PAGE.largeur, PAGE.hauteur, T.fond, 'fond')

  /* --------------------------------------------- coins en L
     En haut a gauche et en bas a droite, sur les deux cotes exterieurs. */
  const MARGE_COIN = 11, COTE_COIN = 14, TRAIT_COIN = 0.8
  rect(MARGE_COIN, MARGE_COIN, COTE_COIN, TRAIT_COIN, T.coin, 'coin')
  rect(MARGE_COIN, MARGE_COIN, TRAIT_COIN, COTE_COIN, T.coin, 'coin')
  const xb = PAGE.largeur - MARGE_COIN - COTE_COIN, yb = PAGE.hauteur - MARGE_COIN - COTE_COIN
  rect(xb, yb + COTE_COIN - TRAIT_COIN, COTE_COIN, TRAIT_COIN, T.coin, 'coin')
  rect(xb + COTE_COIN - TRAIT_COIN, yb, TRAIT_COIN, COTE_COIN, T.coin, 'coin')

  /* ----------------------------------------- le bloc central, en deux temps
     On compose d'abord a partir de y = 0, puis on decale l'ensemble pour le
     centrer dans la bande libre au-dessus du pied : un nom de module sur deux
     lignes ne doit ni coller au pied ni laisser un trou en dessous. */
  const elementsAvantBloc = elements.length

  /* le badge « valide » */
  const RAYON_BADGE = 14
  const yBadge = RAYON_BADGE * (1 + BADGE.amplitude)
  trace([contourRosette(CX, yBadge, RAYON_BADGE)], T.badge, 'badge')
  trace([contourCoche(CX, yBadge, RAYON_BADGE)], T.coche, 'coche')
  const basBadge = 2 * yBadge

  const yMention = basBadge + 13
  texte(capitales(libelle('mention', langue), langue), {
    x: CX, y: yMention, taille: 3.4, couleur: COULEURS.orange, esp: 0.14, role: 'mention',
  })

  const yAtteste = yMention + 15
  texte(libelle('atteste', langue), { x: CX, y: yAtteste, taille: 5, couleur: T.muet, role: 'atteste' })

  /* Le nom, heros de la page : une ligne, reduit s'il le faut, coupe en deux
     lignes seulement s'il reste trop long a la plus petite taille. */
  const LARGEUR_NOM = 236, ESP_NOM = -0.035
  let tNom = 17
  while (tNom > 11 && largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) tNom -= 0.5
  let lignesNom = [String(prenomNom || '')]
  if (largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) lignesNom = couper(prenomNom, tNom, ESP_NOM, LARGEUR_NOM, largeur)
  let yNom = yAtteste + 9 + 0.72 * tNom
  lignesNom.forEach((l, i) => {
    texte(l, { x: CX, y: yNom + i * tNom * 1.08, taille: tNom, couleur: T.heros, esp: ESP_NOM, role: 'nom' })
  })
  yNom += (lignesNom.length - 1) * tNom * 1.08

  /* filet orange court sous le nom */
  const yFilet = yNom + 9.5
  rect(CX - 6, yFilet, 12, 0.8, COULEURS.orange, 'filet-nom')

  const yPhrase = yFilet + 12.5
  texte(libelle('phrase', langue), { x: CX, y: yPhrase, taille: 5, couleur: T.texte, role: 'phrase' })

  /* Le nom exact du module : deux lignes au plus, reduit au-dela. */
  const LARGEUR_MODULE = 220, ESP_MODULE = -0.025
  let tModule = 8.2
  let lignesModule = couper(nomModule || '', tModule, ESP_MODULE, LARGEUR_MODULE, largeur)
  while (lignesModule.length > 2 && tModule > 5.6) {
    tModule -= 0.4
    lignesModule = couper(nomModule || '', tModule, ESP_MODULE, LARGEUR_MODULE, largeur)
  }
  const yModule = yPhrase + 5.5 + 0.72 * tModule
  lignesModule.forEach((l, i) => {
    texte(l, { x: CX, y: yModule + i * tModule * 1.18, taille: tModule, couleur: T.heros, esp: ESP_MODULE, role: 'module' })
  })
  const basBloc = yModule + (lignesModule.length - 1) * tModule * 1.18 + 0.22 * tModule

  /* centrage dans la bande [HAUT_BANDE, BAS_BANDE] */
  const HAUT_BANDE = 16, BAS_BANDE = 156
  const decalage = HAUT_BANDE + Math.max(0, (BAS_BANDE - HAUT_BANDE - basBloc) / 2)
  for (let i = elementsAvantBloc; i < elements.length; i++) {
    elements[i].y += decalage
    if (elements[i].boucles) elements[i].boucles = elements[i].boucles.map((b) => b.map(([x, y]) => [x, y + decalage]))
  }

  /* --------------------------------------------------- pied en 3 colonnes */
  const BAS = 191                      // ligne de base la plus basse du pied
  const ETIQUETTE = 2.6, ESP_ETIQUETTE = 0.14

  // gauche : organisme emetteur, encadre a bordure gauche orange
  const xG = 40
  const hLogo = 6.2, lLogo = hLogo * RATIO_LOGO
  rect(xG, BAS - 22.5, 1.06, 24.5, COULEURS.orange, 'encadre-emetteur')
  texte(capitales(libelle('emetteur', langue), langue), {
    x: xG + 5, y: BAS - 18, taille: ETIQUETTE, couleur: T.muet, esp: ESP_ETIQUETTE, ancre: 'gauche', role: 'etiquette-emetteur',
  })
  elements.push({ type: 'image', source: 'logo', x: xG + 5, y: BAS - 13.6, l: lLogo, h: hLogo, role: 'logo' })
  texte('risinguparis.com', { x: xG + 5, y: BAS, taille: 2.9, couleur: T.muet, ancre: 'gauche', role: 'site' })

  // centre : « Délivré le » et la date du telechargement
  const dateTexte = formaterDate(dateDelivrance, langue)
  if (dateTexte) {
    texte(capitales(libelle('delivreLe', langue), langue), {
      x: CX, y: BAS - 18, taille: ETIQUETTE, couleur: T.muet, esp: ESP_ETIQUETTE, role: 'etiquette-date',
    })
    texte(dateTexte, { x: CX, y: BAS - 9.5, taille: 5.2, couleur: T.heros, esp: -0.01, role: 'date' })
  }

  // droite : signature, filet de signature, nom et fonction
  const xD = 227
  const lSig = 38, hSig = lSig / RATIO_SIGNATURE
  const yLigneSig = BAS - 10.5
  elements.push({ type: 'image', source: 'signature', x: xD - lSig / 2, y: yLigneSig - hSig + 1.6, l: lSig, h: hSig, role: 'signature' })
  rect(xD - 30, yLigneSig, 60, 0.25, T.filet, 'ligne-signature')
  texte(libelle('signataireNom', langue), { x: xD, y: BAS - 5, taille: 3.6, couleur: T.heros, role: 'signataire' })
  texte(libelle('signataireFonction', langue), { x: xD, y: BAS, taille: 2.9, couleur: T.muet, role: 'fonction' })

  /* --------------------------------------------- marque d'exemple (maquette) */
  if (exemple) {
    texte(capitales(libelle('exemple', langue), langue), {
      x: PAGE.largeur - 14, y: 16, taille: 2.4, couleur: T.muet, esp: ESP_ETIQUETTE, ancre: 'droite', role: 'exemple',
    })
  }

  return { page: PAGE, fond: T.fond, elements, date: dateTexte }
}
